import { Injectable, Injector } from '@angular/core';
import { QueryRef, Apollo } from 'apollo-angular';
import { Subscription } from 'rxjs';
import { catchError, map } from 'rxjs/operators';

import { GetGroupsByDistrictGQL } from '../../graphql';
import { AbstractServerSideService } from './abstract/abstract-server-side.service';
import { TelemetryService } from 'src/app/layout-v3/shared/services/telemetry.service';
import { AdminV3Service } from 'src/app/layout-v3/shared/services/admin-v3.service';
import { SeverityError } from 'src/app/layout-v3/shared/enum/severity-error';
import { TelemetryAction } from 'src/app/layout-v3/shared/interfaces/telemetry/telemetry-action';

const queryPropFilter = 'name';

@Injectable({
  providedIn: 'root'
})
export class GroupsService extends AbstractServerSideService {
  // override
  elements!: Array<any>;
  pageTokenAll!: string;
  pageTokenSearch!: string;
  isLoadAllData!: boolean;
  identity = 'name';
  noDataText = 'No Workspace Group';

  // query default variables
  pageToken = '';
  pageSize = '20';
  query!: string;

  // injection
  groupByDistrictGQL!: GetGroupsByDistrictGQL;
  apollo!: Apollo;

  // apollo object query
  groupsQuery!: QueryRef<any>;

  // observable
  private querySubscription!: Subscription;

  telemetryService!: TelemetryService;

  constructor(private injector: Injector) {
    super();
    this.groupByDistrictGQL = this.injector.get(GetGroupsByDistrictGQL);
    this.apollo = this.injector.get(Apollo);
    this.telemetryService = this.injector.get(TelemetryService);
    this.elements = [];
  }

  getQuery(variables: {
    districtId: string;
    isSearching: boolean;
    query: string;
  }): QueryRef<any> {
    if (this.querySubscription) {
      this.querySubscription.unsubscribe();
    }
    this.query = variables.query;
    const isSearching = variables.isSearching;
    this.isLoadAllData = false;

    // active searching
    if (isSearching) {
      this.pageTokenSearch = '';
      this.pageToken = '';
    } else {
      this.pageToken = this.query ? this.pageTokenSearch : this.pageTokenAll;
    }

    const input = {
      districtId: variables.districtId,
      pageSize: this.pageSize,
      pageToken: this.pageToken,
      query: this.query ? queryPropFilter + ':' + this.query + '*' : ''
    };

    this.groupsQuery = this.apollo.watchQuery<any>({
      query: this.groupByDistrictGQL.document,
      variables: input,
      fetchPolicy: 'network-only'
    });

    const telemetryActionData = {
      action: this.telemetryService.getOperationName(this.groupByDistrictGQL),
      variables: input,
    } as TelemetryAction;

    this.querySubscription = this.groupsQuery.valueChanges
      .pipe(
        map(({ data, loading }) => {
          if (data?.groups?.problem) {
            this.telemetryService.logError(data?.groups?.problem, telemetryActionData, SeverityError.ERROR, {districtId: variables?.districtId });
          }
          this.pageToken = data?.groups?.pageToken;
          this.elements = data?.groups?.gGroup;
          this.updateDataToken(this.pageToken);
        }),
        catchError((error) => {
          this.telemetryService.logError(error,telemetryActionData, SeverityError.ERROR, {districtId: variables?.districtId } );
          return error;
        })
      )
      .subscribe();
    return this.groupsQuery;
  }

  unsuscribeQuery(): void {
    if (this.querySubscription) {
      this.querySubscription.unsubscribe();
    }
  }

  private updateDataToken(pageTk: string): void {
    if (!this.query) {
      this.pageTokenAll = pageTk;
    } else {
      this.pageTokenSearch = pageTk;
    }
    this.isLoadAllData = pageTk ? false : true;
  }
}
